import type { CSSProperties } from 'react';

import { cn } from '#/lib/utils';

/**
 * Customer logo rendered as a flat silhouette, same technique as the
 * logo marquee: the image's alpha channel is used as a CSS mask and the
 * div's background colour shows through. This keeps logos monochrome
 * and legible on any section theme, regardless of the source colours.
 */
export function TestimonialLogo({
  image,
  className,
  style,
}: {
  image: { url: string; width: number; height: number; alt?: string | null };
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      role={image.alt ? 'img' : undefined}
      aria-label={image.alt ?? undefined}
      className={cn('shrink-0', className)}
      style={{
        aspectRatio: `${image.width} / ${image.height}`,
        maskImage: `url(${image.url})`,
        WebkitMaskImage: `url(${image.url})`,
        maskSize: 'contain',
        WebkitMaskSize: 'contain',
        maskRepeat: 'no-repeat',
        WebkitMaskRepeat: 'no-repeat',
        maskPosition: 'left center',
        WebkitMaskPosition: 'left center',
        ...style,
      }}
    />
  );
}

export function QuoteMark({ className }: { className?: string }) {
  return (
    <svg
      className={cn('h-auto text-cipher-50', className)}
      viewBox="0 0 67 51.2257"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M36.5898 35.9393C36.5898 32.0364 37.0777 28.1335 38.0534 24.2306C39.1375 20.3277 40.7095 16.75 42.7694 13.4976C44.9377 10.1367 47.5396 7.26375 50.5752 4.87864C53.6108 2.49353 57.1885 0.867314 61.3082 0L65.699 5.04126C62.8803 6.55906 60.3325 8.78155 58.0558 11.7087C55.7791 14.5275 54.5324 17.6173 54.3155 20.9782C58.0016 21.5202 61.0372 23.2549 63.4223 26.182C65.8074 29.0008 67 32.2532 67 35.9393C67 38.1076 66.5663 40.1133 65.699 41.9563C64.9401 43.7994 63.856 45.4256 62.4466 46.835C61.0372 48.2443 59.411 49.3285 57.568 50.0874C55.7249 50.8463 53.7735 51.2257 51.7136 51.2257C47.4854 51.2257 43.9078 49.7621 40.9806 46.835C38.0534 43.9078 36.5898 40.2759 36.5898 35.9393ZM0 35.9393C0 32.0364 0.487864 28.1335 1.46359 24.2306C2.54773 20.3277 4.11974 16.75 6.17961 13.4976C8.23948 10.1367 10.7872 7.26375 13.8228 4.87864C16.9668 2.49353 20.5987 0.867314 24.7184 0L28.9466 5.04126C26.1278 6.55906 23.5801 8.78155 21.3034 11.7087C19.1351 14.5275 17.9426 17.6173 17.7257 20.9782C21.4118 21.5202 24.4474 23.2549 26.8325 26.182C29.2176 29.0008 30.4102 32.2532 30.4102 35.9393C30.4102 38.1076 29.9765 40.1133 29.1092 41.9563C28.3503 43.7994 27.2662 45.4256 25.8568 46.835C24.4474 48.2443 22.767 49.3285 20.8155 50.0874C18.9725 50.8463 17.021 51.2257 14.9612 51.2257C12.9013 51.2257 10.9498 50.8463 9.1068 50.0874C7.26375 49.3285 5.63754 48.2443 4.22816 46.835C2.92718 45.4256 1.89725 43.7994 1.13835 41.9563C0.37945 40.1133 0 38.1076 0 35.9393Z"
        fill="currentColor"
      />
    </svg>
  );
}

export function UeVerifiedBadge({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M19 16L26 0H21L15.8667 11.7333L14 8H9L13 16H19Z"
        fill="currentColor"
      />
      <path
        d="M16 27C22.0751 27 27 22.0751 27 16H32C32 24.8366 24.8366 32 16 32C7.16344 32 0 24.8366 0 16H5C5 22.0751 9.92487 27 16 27Z"
        fill="currentColor"
      />
    </svg>
  );
}
